import { Link } from 'react-router-dom';
import BorderlessSelect from '../../../components/BorderlessSelect';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  useArchiveScheduleProgram,
  useCreateScheduleProgram,
  useRestoreScheduleProgram,
  useScheduleProgramRegistrations,
  useSchedulePrograms,
  useTenantStudioSettings,
  useTenantOnboardingAccess,
  useUpdateScheduleProgramRegistration,
  useUpdateScheduleProgram,
} from '@tark/shared/hooks';
import { Button, Card, CardContent, Input, Textarea } from '@tark/shared/components';
import { apiClient, cn } from '@tark/shared/lib';
import {
  Archive,
  BookOpenCheck,
  CheckCircle2,
  Copy,
  CreditCard,
  Download,
  Filter,
  FileText,
  Loader2,
  MapPin,
  Plus,
  ReceiptText,
  RotateCcw,
  Search,
  Users,
  X,
} from 'lucide-react';
import type { TenantMemberSummary, TenantProgram, TenantProgramRegistration, TenantProgramRegistrationUpdatePayload } from '@tark/shared/types';
import CoachPageHeader from '../../../components/CoachPageHeader';
import ProgramCatalogForm from './ProgramCatalogForm';
import type { ProgramCatalogCoachOption, ProgramCatalogFormState, ProgramCatalogSubmitIntent } from './programCatalogTypes';
import {
  buildProgramCatalogPayload,
  defaultProgramCatalogForm,
  formatCatalogDateRange,
  formatScheduleCodeLabel,
  programIsPublished,
  programTypeOptions,
  summarizeProgramOptions,
  validateProgramCatalogForm,
} from './programCatalogUtils';

type CatalogStatusFilter = 'all' | 'published' | 'draft' | 'inactive';
type CatalogModalMode = 'create' | 'edit' | null;
type RegistrationStatusFilter = 'all' | 'needs-action' | 'registered' | 'waitlisted' | 'cancelled';
type RegistrationPaymentFilter = 'all' | 'due' | 'paid' | 'partial' | 'refunded';
type PaymentFormState = {
  paymentStatus: string;
  paymentMethodLabel: string;
  paidAmount: string;
  paidAt: string;
  refundedAmount: string;
  refundedAt: string;
  externalReference: string;
  externalPaymentUrl: string;
  receiptUrl: string;
  refundReason: string;
  paymentNote: string;
  note: string;
};

type RegistrationMoneySummary = {
  amountDue: number;
  paidAmount: number;
  balanceAmount: number;
  refundedAmount: number;
  dueCount: number;
  paidCount: number;
  partialCount: number;
  refundedCount: number;
  currencyCode: string;
};

export default function CoachProgramCatalogPanel({
  enabled,
  canManageSchedule,
  onProgramsChanged,
  mode = 'catalog',
}: {
  enabled: boolean;
  canManageSchedule: boolean;
  onProgramsChanged?: () => Promise<unknown> | unknown;
  mode?: 'catalog' | 'payments';
}) {
  const { data: programs = [], isLoading, isError, refetch } = useSchedulePrograms(enabled);
  const {
    data: registrations = [],
    isLoading: registrationsLoading,
    refetch: refetchRegistrations,
  } = useScheduleProgramRegistrations(enabled);
  const { data: studioSettings = null } = useTenantStudioSettings(enabled);
  const { access } = useTenantOnboardingAccess(enabled);
  const createProgram = useCreateScheduleProgram();
  const updateProgram = useUpdateScheduleProgram();
  const archiveProgram = useArchiveScheduleProgram();
  const restoreProgram = useRestoreScheduleProgram();
  const updateRegistration = useUpdateScheduleProgramRegistration();
  const [modalMode, setModalMode] = useState<CatalogModalMode>(null);
  const [editingProgramId, setEditingProgramId] = useState<number | null>(null);
  const [formState, setFormState] = useState<ProgramCatalogFormState>(() => defaultProgramCatalogForm());
  const [formError, setFormError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<CatalogStatusFilter>('all');
  const [typeFilter, setTypeFilter] = useState('all');

  const editingProgram = editingProgramId
    ? programs.find((program) => program.programId === editingProgramId) ?? null
    : null;
  const isSaving = createProgram.isPending || updateProgram.isPending;
  const isArchiving = archiveProgram.isPending;
  const isRestoring = restoreProgram.isPending;
  const publishedCount = programs.filter((program) => program.active && programIsPublished(program)).length;
  const draftCount = programs.filter((program) => program.active && !programIsPublished(program)).length;
  const inactiveCount = programs.filter((program) => !program.active).length;
  const coachOptions = useMemo(
    () => buildCoachOptions(access?.members ?? []),
    [access?.members]
  );

  const typeOptions = useMemo(() => {
    const availableTypes = new Set(programs.map((program) => program.programType).filter(Boolean));
    return programTypeOptions.filter((option) => option.value && availableTypes.has(option.value));
  }, [programs]);

  const filteredPrograms = useMemo(() => {
    const needle = searchTerm.trim().toLowerCase();
    return programs.filter((program) => {
      if (statusFilter !== 'all' && getCatalogStatus(program) !== statusFilter) {
        return false;
      }

      if (typeFilter !== 'all' && program.programType !== typeFilter) {
        return false;
      }

      if (!needle) {
        return true;
      }

      const policy = program.registrationPolicy;
      return [
        program.name,
        policy?.publicTitle,
        policy?.seasonLabel,
        policy?.locationLabel,
        policy?.audienceLabel,
        formatScheduleCodeLabel(program.programType),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(needle);
    });
  }, [programs, searchTerm, statusFilter, typeFilter]);

  const hasFilters = Boolean(searchTerm.trim()) || statusFilter !== 'all' || typeFilter !== 'all';

  const resetForm = () => {
    setEditingProgramId(null);
    setFormState(defaultProgramCatalogForm());
    setFormError(null);
  };

  const openCreateForm = () => {
    if (!canManageSchedule) {
      return;
    }
    resetForm();
    setModalMode('create');
  };

  const openEditForm = (program: TenantProgram) => {
    if (!canManageSchedule) {
      return;
    }
    setEditingProgramId(program.programId);
    setFormState(defaultProgramCatalogForm(program));
    setFormError(null);
    setModalMode('edit');
  };

  const closeModal = () => {
    setModalMode(null);
    resetForm();
  };

  const refreshPrograms = async () => {
    await Promise.all([refetch(), refetchRegistrations(), Promise.resolve(onProgramsChanged?.())]);
  };

  const handleSubmit = async (intent: ProgramCatalogSubmitIntent) => {
    const validationError = validateProgramCatalogForm(formState, intent);
    if (validationError) {
      setFormError(validationError);
      return;
    }

    setFormError(null);
    try {
      const payload = buildProgramCatalogPayload(formState, intent);
      if (editingProgramId) {
        await updateProgram.mutateAsync({ programId: editingProgramId, payload });
      } else {
        await createProgram.mutateAsync(payload);
      }
      await refreshPrograms();
      closeModal();
    } catch (error) {
      console.error('Failed to save coach class catalog', error);
      setFormError('Could not save this class catalog. Check the details and try again.');
    }
  };

  const handleArchiveProgram = async () => {
    if (!editingProgram) {
      return;
    }

    if (!window.confirm(`Make "${editingProgram.name}" inactive? Existing sessions stay linked, but families will not see it publicly.`)) {
      return;
    }

    try {
      await archiveProgram.mutateAsync(editingProgram.programId);
      await refreshPrograms();
      closeModal();
    } catch (error) {
      console.error('Failed to archive coach class catalog', error);
      setFormError('Could not make this class inactive right now.');
    }
  };

  const handleRestoreProgram = async () => {
    if (!editingProgram) {
      return;
    }

    try {
      await restoreProgram.mutateAsync(editingProgram.programId);
      await refreshPrograms();
      closeModal();
    } catch (error) {
      console.error('Failed to restore coach class catalog', error);
      setFormError('Could not reactivate this class right now.');
    }
  };

  const clearFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setTypeFilter('all');
  };

  const handleUpdateRegistration = async (
    registrationId: number,
    payload: TenantProgramRegistrationUpdatePayload
  ) => {
    await updateRegistration.mutateAsync({ registrationId, payload });
    await refreshPrograms();
  };

  if (mode === 'payments') {
    return (
      <RegistrationQueuePanel
        registrations={registrations}
        isLoading={registrationsLoading}
        isUpdating={updateRegistration.isPending}
        canManageSchedule={canManageSchedule}
        onUpdateRegistration={handleUpdateRegistration}
      />
    );
  }

  return (
    <div className="space-y-5">
      <CoachPageHeader
        title="Classes"
        subtitle="Class catalog"
        action={
          canManageSchedule ? (
            <Button leftIcon={<Plus className="h-4 w-4" />} onClick={openCreateForm}>
              Create class
            </Button>
          ) : null
        }
        stats={
          <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
            <CatalogStat label="Total" value={programs.length} />
            <CatalogStat label="Published" value={publishedCount} />
            <CatalogStat label="Draft" value={draftCount} />
            <CatalogStat label="Inactive" value={inactiveCount} />
          </div>
        }
      />

      <Card variant="default" padding="none" className="overflow-hidden">
        <CardContent className="space-y-4">
          <div className="grid gap-3 lg:grid-cols-[minmax(220px,1fr)_180px_190px_auto] lg:items-end">
            <Input
              label="Search"
              placeholder="Program, season, location"
              leftIcon={<Search className="h-4 w-4" />}
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
            />
            <SelectFilter
              label="Status"
              value={statusFilter}
              options={[
                { value: 'all', label: 'All' },
                { value: 'published', label: 'Published' },
                { value: 'draft', label: 'Draft' },
                { value: 'inactive', label: 'Inactive' },
              ]}
              onChange={(value) => setStatusFilter(value as CatalogStatusFilter)}
            />
            <SelectFilter
              label="Type"
              value={typeFilter}
              options={[
                { value: 'all', label: 'All types' },
                ...typeOptions.map((option) => ({ value: option.value, label: option.label })),
              ]}
              onChange={setTypeFilter}
            />
            <Button
              type="button"
              variant="outline"
              leftIcon={<Filter className="h-4 w-4" />}
              onClick={clearFilters}
              disabled={!hasFilters}
            >
              Clear
            </Button>
          </div>

          {isLoading ? (
            <div className="flex items-center gap-3 rounded-lg border border-border bg-surface-elevated/30 p-4">
              <Loader2 className="h-4 w-4 animate-spin text-primary" />
              <p className="text-sm font-medium text-text-muted">Loading classes...</p>
            </div>
          ) : null}

          {isError ? (
            <div className="flex flex-col gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4 text-amber-700 md:flex-row md:items-center md:justify-between">
              <p className="text-sm font-medium">Classes could not load right now.</p>
              <Button size="sm" variant="outline" onClick={() => refetch()}>
                Retry
              </Button>
            </div>
          ) : null}

          {!isLoading && !isError ? (
            programs.length > 0 ? (
              filteredPrograms.length > 0 ? (
                <ProgramCatalogTable programs={filteredPrograms} canManageSchedule={canManageSchedule} onOpenProgram={openEditForm} />
              ) : (
                <EmptyCatalogState
                  title="No matches"
                  description="Clear filters to see the full catalog."
                  actionLabel="Clear Filters"
                  onAction={clearFilters}
                />
              )
            ) : (
              <EmptyCatalogState
                title="No classes yet"
                description="Start with the season and class options families will choose from."
                actionLabel="Create class"
                onAction={openCreateForm}
                disabled={!canManageSchedule}
              />
            )
          ) : null}
        </CardContent>
      </Card>

      <Link to="/payments" className="inline-flex min-h-[44px] items-center text-sm font-semibold text-primary">Registrations & payments →</Link>

      <CatalogModal
        open={modalMode !== null}
        title={modalMode === 'create' ? 'Create class' : 'Manage Class'}
        subtitle={editingProgram ? editingProgram.registrationPolicy?.publicTitle || editingProgram.name : 'Draft first, publish when ready.'}
        status={editingProgram ? getCatalogStatus(editingProgram) : 'draft'}
        onClose={closeModal}
        actionSlot={
          editingProgram ? (
            editingProgram.active ? (
              <Button
                type="button"
                size="sm"
                variant="outline"
                leftIcon={<Archive className="h-4 w-4" />}
                isLoading={isArchiving}
                onClick={handleArchiveProgram}
              >
                Make Inactive
              </Button>
            ) : (
              <Button
                type="button"
                size="sm"
                variant="outline"
                leftIcon={<RotateCcw className="h-4 w-4" />}
                isLoading={isRestoring}
                onClick={handleRestoreProgram}
              >
                Restore
              </Button>
            )
          ) : null
        }
      >
        <ProgramCatalogForm
          formState={formState}
          editingProgram={editingProgram}
          studioSettings={studioSettings}
          coachOptions={coachOptions}
          isSaving={isSaving}
          error={formError}
          onChange={setFormState}
          onSubmit={handleSubmit}
          onCancel={closeModal}
        />
      </CatalogModal>
    </div>
  );
}

function RegistrationQueuePanel({
  registrations,
  isLoading,
  isUpdating,
  canManageSchedule,
  onUpdateRegistration,
}: {
  registrations: TenantProgramRegistration[];
  isLoading: boolean;
  isUpdating: boolean;
  canManageSchedule: boolean;
  onUpdateRegistration: (registrationId: number, payload: TenantProgramRegistrationUpdatePayload) => Promise<void>;
}) {
  const [searchTerm, setSearchTerm] = useState('');
  const [programFilter, setProgramFilter] = useState('all');
  const [sessionFilter, setSessionFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState<RegistrationStatusFilter>('all');
  const [paymentFilter, setPaymentFilter] = useState<RegistrationPaymentFilter>('all');
  const [paymentRegistration, setPaymentRegistration] = useState<TenantProgramRegistration | null>(null);
  const [paymentForm, setPaymentForm] = useState<PaymentFormState>(() => defaultPaymentForm());
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const pendingCount = registrations.filter((registration) =>
    registrationNeedsAction(registration)
  ).length;
  const paidCount = registrations.filter((registration) => (registration.paymentStatus || '').toUpperCase() === 'PAID').length;
  const waitlistCount = registrations.filter((registration) => (registration.registrationStatus || '').toUpperCase() === 'WAITLISTED').length;
  const moneySummary = useMemo(() => summarizeRegistrationMoney(registrations), [registrations]);

  const programOptions = useMemo(() => {
    const options = new Map<string, string>();
    registrations.forEach((registration) => {
      options.set(String(registration.programId), registration.publicTitle || registration.programName || `Class ${registration.programId}`);
    });
    return Array.from(options.entries()).map(([value, label]) => ({ value, label }));
  }, [registrations]);

  const sessionOptions = useMemo(() => {
    const options = new Map<string, string>();
    registrations.forEach((registration) => {
      const key = registration.selectedSessionOptionId || registration.selectedSessionLabel;
      if (key) {
        options.set(key, registration.selectedSessionLabel || key);
      }
    });
    return Array.from(options.entries()).map(([value, label]) => ({ value, label }));
  }, [registrations]);

  const filteredRegistrations = useMemo(() => {
    const needle = searchTerm.trim().toLowerCase();
    return registrations.filter((registration) => {
      if (programFilter !== 'all' && String(registration.programId) !== programFilter) {
        return false;
      }
      if (sessionFilter !== 'all' && (registration.selectedSessionOptionId || registration.selectedSessionLabel) !== sessionFilter) {
        return false;
      }
      if (statusFilter !== 'all' && !matchesRegistrationStatus(registration, statusFilter)) {
        return false;
      }
      if (paymentFilter !== 'all' && !matchesPaymentStatus(registration, paymentFilter)) {
        return false;
      }
      if (!needle) {
        return true;
      }
      return [
        registration.athleteDisplayName,
        registration.guardianDisplayName,
        registration.guardianEmail,
        registration.guardianPhone,
        registration.publicTitle,
        registration.programName,
        registration.selectedSessionLabel,
        registration.selectedCategoryLabel,
        registration.invoiceNumber,
        registration.receiptNumber,
        registration.externalReference,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(needle);
    });
  }, [paymentFilter, programFilter, registrations, searchTerm, sessionFilter, statusFilter]);

  const hasFilters =
    Boolean(searchTerm.trim()) ||
    programFilter !== 'all' ||
    sessionFilter !== 'all' ||
    statusFilter !== 'all' ||
    paymentFilter !== 'all';

  const clearFilters = () => {
    setSearchTerm('');
    setProgramFilter('all');
    setSessionFilter('all');
    setStatusFilter('all');
    setPaymentFilter('all');
  };

  const openPaymentModal = (registration: TenantProgramRegistration) => {
    setPaymentRegistration(registration);
    setPaymentForm(defaultPaymentForm(registration));
    setPaymentError(null);
  };

  const closePaymentModal = () => {
    setPaymentRegistration(null);
    setPaymentForm(defaultPaymentForm());
    setPaymentError(null);
  };

  const handleSubmitPayment = async () => {
    if (!paymentRegistration) {
      return;
    }
    const paidAmount = paymentForm.paidAmount.trim() ? Number(paymentForm.paidAmount) : undefined;
    if (paidAmount !== undefined && (!Number.isFinite(paidAmount) || paidAmount < 0)) {
      setPaymentError('Amount must be zero or more.');
      return;
    }
    const refundedAmount = paymentForm.refundedAmount.trim() ? Number(paymentForm.refundedAmount) : undefined;
    if (refundedAmount !== undefined && (!Number.isFinite(refundedAmount) || refundedAmount < 0)) {
      setPaymentError('Refund must be zero or more.');
      return;
    }
    try {
      await onUpdateRegistration(paymentRegistration.registrationId, {
        paymentStatus: paymentForm.paymentStatus,
        paymentMethodLabel: paymentForm.paymentMethodLabel.trim() || null,
        paidAmount: paidAmount ?? null,
        paidAt: paymentForm.paidAt ? new Date(paymentForm.paidAt).toISOString() : null,
        refundedAmount: refundedAmount ?? null,
        refundedAt: paymentForm.refundedAt ? new Date(paymentForm.refundedAt).toISOString() : null,
        refundReason: paymentForm.refundReason.trim() || null,
        externalReference: paymentForm.externalReference.trim() || null,
        externalPaymentUrl: paymentForm.externalPaymentUrl.trim() || null,
        receiptUrl: paymentForm.receiptUrl.trim() || null,
        paymentNote: paymentForm.paymentNote.trim() || null,
        note: paymentForm.note.trim() || null,
      });
      closePaymentModal();
    } catch (error) {
      console.error('Failed to update registration payment', error);
      setPaymentError('Payment details could not be saved.');
    }
  };

  const handleExport = () => {
    exportRegistrationsCsv(filteredRegistrations);
  };

  return (
    <section className="rounded-lg border border-border bg-surface p-4 shadow-[var(--shadow-card)] md:p-5">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-primary">Orders</p>
          <h2 className="mt-1 text-xl font-semibold tracking-tight text-text">Registrations & payments</h2>
        </div>
        <div className="flex flex-wrap gap-2">
          <CatalogStat label="Total" value={registrations.length} />
          <CatalogStat label="Needs action" value={pendingCount} />
          <CatalogStat label="Paid" value={paidCount} />
          <CatalogStat label="Waitlist" value={waitlistCount} />
        </div>
      </div>

      <RegistrationMoneyStrip summary={moneySummary} />

      <div className="tk-registration-filters mt-4 grid gap-3 xl:items-end">
        <Input
          label="Search"
          placeholder="Student, parent, class, reference"
          leftIcon={<Search className="h-4 w-4" />}
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.target.value)}
        />
        <SelectFilter
          label="Class"
          value={programFilter}
          options={[{ value: 'all', label: 'All classes' }, ...programOptions]}
          onChange={setProgramFilter}
        />
        <SelectFilter
          label="Session"
          value={sessionFilter}
          options={[{ value: 'all', label: 'All sessions' }, ...sessionOptions]}
          onChange={setSessionFilter}
        />
        <SelectFilter
          label="Status"
          value={statusFilter}
          options={[
            { value: 'all', label: 'All' },
            { value: 'needs-action', label: 'Needs action' },
            { value: 'registered', label: 'Confirmed' },
            { value: 'waitlisted', label: 'Waitlist' },
            { value: 'cancelled', label: 'Cancelled' },
          ]}
          onChange={(value) => setStatusFilter(value as RegistrationStatusFilter)}
        />
        <SelectFilter
          label="Payment"
          value={paymentFilter}
          options={[
            { value: 'all', label: 'All' },
            { value: 'due', label: 'Due' },
            { value: 'paid', label: 'Paid' },
            { value: 'partial', label: 'Partial' },
            { value: 'refunded', label: 'Refunded' },
          ]}
          onChange={(value) => setPaymentFilter(value as RegistrationPaymentFilter)}
        />
        <div className="tk-registration-actions flex gap-2">
          <Button type="button" variant="outline" leftIcon={<Filter className="h-4 w-4" />} disabled={!hasFilters} onClick={clearFilters}>
            Clear
          </Button>
          <Button
            type="button"
            variant="outline"
            leftIcon={<Download className="h-4 w-4" />}
            disabled={filteredRegistrations.length === 0}
            onClick={handleExport}
          >
            Export
          </Button>
        </div>
      </div>

      <div className="mt-4 overflow-hidden rounded-lg border border-border">
        {isLoading ? (
          <div className="flex items-center gap-3 bg-surface-elevated/30 p-4">
            <Loader2 className="h-4 w-4 animate-spin text-primary" />
            <p className="text-sm font-medium text-text-muted">Loading registrations...</p>
          </div>
        ) : filteredRegistrations.length ? (
          <div>
            <div className="hidden overflow-x-auto xl:block">
              <table className="tk-registration-table w-full min-w-[1100px] border-collapse text-left">
                <thead className="border-b border-border bg-surface-elevated/50">
                  <tr className="text-[11px] font-semibold uppercase tracking-[0.14em] text-text-subtle">
                    <th className="px-4 py-3">Student</th>
                    <th className="px-4 py-3">Class</th>
                    <th className="px-4 py-3">Payment</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border bg-surface">
                  {filteredRegistrations.map((registration) => (
                    <RegistrationRow
                      key={registration.registrationId}
                      registration={registration}
                      canManageSchedule={canManageSchedule}
                      isUpdating={isUpdating}
                      onUpdateRegistration={onUpdateRegistration}
                      onOpenPayment={openPaymentModal}
                    />
                  ))}
                </tbody>
              </table>
            </div>
            <div className="divide-y divide-border xl:hidden">
              {filteredRegistrations.map((registration) => (
                <RegistrationMobileRow
                  key={registration.registrationId}
                  registration={registration}
                  canManageSchedule={canManageSchedule}
                  isUpdating={isUpdating}
                  onUpdateRegistration={onUpdateRegistration}
                  onOpenPayment={openPaymentModal}
                />
              ))}
            </div>
          </div>
        ) : (
          <EmptyCatalogState
            title={registrations.length ? 'No matching registrations' : 'No registrations yet'}
            description={registrations.length ? 'Clear filters to see every registration.' : 'Published classes will collect family signups here.'}
            actionLabel={registrations.length ? 'Clear Filters' : undefined}
            onAction={registrations.length ? clearFilters : undefined}
          />
        )}
      </div>

      <PaymentTrackingModal
        registration={paymentRegistration}
        formState={paymentForm}
        error={paymentError}
        isSaving={isUpdating}
        onChange={setPaymentForm}
        onClose={closePaymentModal}
        onSubmit={handleSubmitPayment}
      />
    </section>
  );
}

function RegistrationMoneyStrip({ summary }: { summary: RegistrationMoneySummary }) {
  return (
    <div className="mt-4 grid gap-3 md:grid-cols-4">
      <MoneyTile
        icon={<ReceiptText className="h-4 w-4" />}
        label="Balance due"
        value={formatMoney(summary.balanceAmount, summary.currencyCode)}
        detail={`${summary.dueCount} due`}
        tone={summary.balanceAmount > 0 ? 'amber' : 'neutral'}
      />
      <MoneyTile
        icon={<CreditCard className="h-4 w-4" />}
        label="Paid"
        value={formatMoney(summary.paidAmount, summary.currencyCode)}
        detail={`${summary.paidCount} paid`}
      />
      <MoneyTile
        icon={<CreditCard className="h-4 w-4" />}
        label="Partial"
        value={String(summary.partialCount)}
        detail="orders"
      />
      <MoneyTile
        icon={<RotateCcw className="h-4 w-4" />}
        label="Refunded"
        value={formatMoney(summary.refundedAmount, summary.currencyCode)}
        detail={`${summary.refundedCount} refunds`}
      />
    </div>
  );
}

function MoneyTile({
  icon,
  label,
  value,
  detail,
  tone = 'neutral',
}: {
  icon: ReactNode;
  label: string;
  value: string;
  detail: string;
  tone?: 'neutral' | 'amber';
}) {
  const styles = tone === 'amber' ? 'border-amber-200 bg-amber-50/70 text-amber-700' : 'border-border bg-surface text-primary';
  return (
    <div className="rounded-lg border border-border bg-surface px-4 py-3">
      <div className="flex items-center gap-2">
        <span className={cn('inline-flex h-8 w-8 items-center justify-center rounded-lg border', styles)}>{icon}</span>
        <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-text-subtle">{label}</p>
      </div>
      <div className="mt-3 flex items-end justify-between gap-3">
        <p className="text-xl font-semibold tracking-tight text-text">{value}</p>
        <p className="pb-0.5 text-xs font-semibold text-text-muted">{detail}</p>
      </div>
    </div>
  );
}

function RegistrationRow({
  registration,
  canManageSchedule,
  isUpdating,
  onUpdateRegistration,
  onOpenPayment,
}: {
  registration: TenantProgramRegistration;
  canManageSchedule: boolean;
  isUpdating: boolean;
  onUpdateRegistration: (registrationId: number, payload: TenantProgramRegistrationUpdatePayload) => Promise<void>;
  onOpenPayment: (registration: TenantProgramRegistration) => void;
}) {
  const paymentStatus = (registration.paymentStatus || '').toUpperCase();
  const paid = paymentStatus === 'PAID';
  const paymentMeta = formatPaymentMeta(registration);

  return (
    <tr className="transition-colors hover:bg-surface-elevated/30">
      <td className="px-4 py-4">
        <p className="font-semibold text-text">{registration.athleteDisplayName}</p>
        <p className="mt-1 max-w-[220px] truncate text-sm text-text-muted">
          {[registration.guardianDisplayName, registration.guardianPhone || registration.guardianEmail].filter(Boolean).join(' / ') || 'Parent'}
        </p>
      </td>
      <td className="px-4 py-4">
        <p className="max-w-[300px] truncate text-sm font-semibold text-text">{registration.publicTitle || registration.programName || 'Class'}</p>
        <p className="mt-1 max-w-[360px] truncate text-sm text-text-muted">
          {[registration.selectedSessionLabel, registration.selectedCategoryLabel, registration.daysPreference].filter(Boolean).join(' / ') || '-'}
        </p>
      </td>
      <td className="px-4 py-4">
        <p className="text-sm font-semibold text-text">{formatRegistrationAmount(registration)}</p>
        <p className="mt-1 max-w-[220px] truncate text-xs font-medium text-text-muted">
          {paymentMeta}
        </p>
      </td>
      <td className="px-4 py-4">
        <div className="flex flex-wrap gap-2">
          <RegistrationPill value={registration.registrationStatus} />
          <RegistrationPill value={registration.paymentStatus} tone={paid ? 'paid' : 'pending'} />
        </div>
      </td>
      <td className="px-4 py-4">
        <RegistrationActions
          registration={registration}
          canManageSchedule={canManageSchedule}
          isUpdating={isUpdating}
          onUpdateRegistration={onUpdateRegistration}
          onOpenPayment={onOpenPayment}
        />
      </td>
    </tr>
  );
}

function RegistrationMobileRow({
  registration,
  canManageSchedule,
  isUpdating,
  onUpdateRegistration,
  onOpenPayment,
}: {
  registration: TenantProgramRegistration;
  canManageSchedule: boolean;
  isUpdating: boolean;
  onUpdateRegistration: (registrationId: number, payload: TenantProgramRegistrationUpdatePayload) => Promise<void>;
  onOpenPayment: (registration: TenantProgramRegistration) => void;
}) {
  const paid = (registration.paymentStatus || '').toUpperCase() === 'PAID';
  const paymentMeta = formatPaymentMeta(registration);

  return (
    <div className="space-y-3 bg-surface p-4">
      <div className="tk-registration-mobile-heading flex flex-wrap items-start justify-between gap-3">
        <div className="tk-registration-identity min-w-0">
          <p className="font-semibold text-text">{registration.athleteDisplayName}</p>
          <p className="mt-1 text-sm text-text-muted">{registration.guardianDisplayName || registration.guardianPhone || 'Parent'}</p>
        </div>
        <div className="tk-registration-statuses flex flex-wrap gap-2">
          <RegistrationPill value={registration.registrationStatus} />
          <RegistrationPill value={registration.paymentStatus} tone={paid ? 'paid' : 'pending'} />
        </div>
      </div>
      <div className="rounded-lg border border-border bg-surface px-3 py-2">
        <p className="text-sm font-semibold text-text">{registration.publicTitle || registration.programName || 'Class'}</p>
        <p className="mt-1 text-sm text-text-muted">
          {[registration.selectedSessionLabel, registration.selectedCategoryLabel, registration.daysPreference].filter(Boolean).join(' / ') || '-'}
        </p>
        <p className="mt-2 text-sm font-semibold text-text">{formatRegistrationAmount(registration)}</p>
        <p className="mt-1 text-xs font-medium text-text-muted">{paymentMeta}</p>
      </div>
      <RegistrationActions
        registration={registration}
        canManageSchedule={canManageSchedule}
        isUpdating={isUpdating}
        onUpdateRegistration={onUpdateRegistration}
        onOpenPayment={onOpenPayment}
      />
    </div>
  );
}

function RegistrationActions({
  registration,
  canManageSchedule,
  isUpdating,
  onUpdateRegistration,
  onOpenPayment,
}: {
  registration: TenantProgramRegistration;
  canManageSchedule: boolean;
  isUpdating: boolean;
  onUpdateRegistration: (registrationId: number, payload: TenantProgramRegistrationUpdatePayload) => Promise<void>;
  onOpenPayment: (registration: TenantProgramRegistration) => void;
}) {
  const registrationStatus = (registration.registrationStatus || '').toUpperCase();
  const cancelled = registrationStatus === 'CANCELLED';
  const waitlisted = registrationStatus === 'WAITLISTED';

  return <div className="tk-registration-actions flex flex-wrap items-start gap-2 xl:justify-end">
      <Button
        size="sm"
        variant="outline"
        leftIcon={<CreditCard className="h-4 w-4" />}
        disabled={!canManageSchedule || isUpdating || cancelled}
        onClick={() => onOpenPayment(registration)}
      >
        Payment
      </Button>
      <details><summary className="min-h-[36px] cursor-pointer rounded-lg border border-border px-3 py-2 text-xs text-text-muted">More</summary><div className="mt-2 flex flex-wrap gap-2">
      <Button
        size="sm"
        variant="outline"
        leftIcon={<CheckCircle2 className="h-4 w-4" />}
        disabled={!canManageSchedule || isUpdating || cancelled || registrationStatus === 'REGISTERED'}
        onClick={() => void onUpdateRegistration(registration.registrationId, { registrationStatus: 'REGISTERED' })}
      >
        Confirm
      </Button>
      <Button
        size="sm"
        variant="outline"
        disabled={!canManageSchedule || isUpdating || cancelled || waitlisted}
        onClick={() => void onUpdateRegistration(registration.registrationId, { registrationStatus: 'WAITLISTED' })}
      >
        Waitlist
      </Button>
      <Button
        size="sm"
        variant="outline"
        disabled={!canManageSchedule || isUpdating || cancelled}
        onClick={() => void onUpdateRegistration(registration.registrationId, { registrationStatus: 'CANCELLED' })}
      >
        Cancel
      </Button>
      </div></details>
    </div>;

}

function PaymentTrackingModal({
  registration,
  formState,
  error,
  isSaving,
  onChange,
  onClose,
  onSubmit,
}: {
  registration: TenantProgramRegistration | null;
  formState: PaymentFormState;
  error: string | null;
  isSaving: boolean;
  onChange: (state: PaymentFormState) => void;
  onClose: () => void;
  onSubmit: () => Promise<void>;
}) {
  const [receiptCopied, setReceiptCopied] = useState(false);

  useEffect(() => {
    setReceiptCopied(false);
  }, [registration?.registrationId]);

  if (!registration) {
    return null;
  }

  const update = (patch: Partial<PaymentFormState>) => onChange({ ...formState, ...patch });
  const amountDue = moneyValue(registration.amountDue ?? registration.priceAmount);
  const balanceAmount = moneyValue(registration.balanceAmount);
  const refundedAmount = moneyValue(registration.refundedAmount);
  const receiptDocumentUrl = buildReceiptDocumentUrl(registration);
  const publicReceiptDocumentUrl = buildPublicReceiptDocumentUrl(registration);
  const waiverDocumentUrl = buildWaiverDocumentUrl(registration);

  const openDocument = (url: string | null) => {
    if (!url || typeof window === 'undefined') {
      return;
    }
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const copyReceiptLink = async () => {
    if (!publicReceiptDocumentUrl || typeof navigator === 'undefined' || !navigator.clipboard) {
      return;
    }
    await navigator.clipboard.writeText(publicReceiptDocumentUrl);
    setReceiptCopied(true);
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-950/45 px-3 py-6 backdrop-blur-sm">
      <div className="flex max-h-[92vh] w-full max-w-3xl flex-col overflow-hidden rounded-xl border border-border bg-surface shadow-2xl">
        <div className="flex items-start justify-between gap-3 border-b border-border px-4 py-4 md:px-5">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <ReceiptText className="h-5 w-5 text-primary" />
              <p className="text-lg font-semibold text-text">Record payment</p>
            </div>
            <p className="mt-1 truncate text-sm font-medium text-text-muted">
              {registration.athleteDisplayName} / {registration.publicTitle || registration.programName || 'Class'}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-border text-text-muted transition-colors hover:bg-surface-elevated hover:text-text"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="overflow-y-auto p-4 md:p-5">
          <div className="mb-4 rounded-lg border border-border bg-surface-elevated/35 p-3">
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <MiniMoney label="Due" value={formatMoney(amountDue, registration.currencyCode)} />
              <MiniMoney label="Balance" value={formatMoney(balanceAmount, registration.currencyCode)} />
            </div>
            <details className="pro-payment-records"><summary>Receipt & invoice details</summary>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <MiniMoney label="Invoice" value={registration.invoiceNumber || '-'} />
              <MiniMoney label="Receipt" value={registration.receiptNumber || '-'} />
              <MiniMoney
                label="Receipt share"
                value={registration.receiptShareEnabled && registration.receiptShareToken ? 'Ready' : '-'}
              />
              <MiniMoney
                label="Sequence"
                value={
                  registration.invoiceSequenceYear && registration.invoiceSequenceNumber
                    ? `${registration.invoiceSequenceYear}-${String(registration.invoiceSequenceNumber).padStart(6, '0')}`
                    : '-'
                }
              />
            </div>
            </details>
            {refundedAmount > 0 ? (
              <p className="mt-3 text-xs font-semibold text-text-muted">
                Refunded {formatMoney(refundedAmount, registration.currencyCode)}
                {registration.refundedAt ? ` / ${formatDateTime(registration.refundedAt)}` : ''}
                {registration.refundReason ? ` / ${registration.refundReason}` : ''}
              </p>
            ) : null}
            {receiptDocumentUrl || waiverDocumentUrl ? (
              <div className="mt-3 flex flex-wrap gap-2">
                {receiptDocumentUrl ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    leftIcon={<ReceiptText className="h-4 w-4" />}
                    onClick={() => openDocument(receiptDocumentUrl)}
                  >
                    Receipt
                  </Button>
                ) : null}
                {publicReceiptDocumentUrl ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    leftIcon={<Copy className="h-4 w-4" />}
                    onClick={() => void copyReceiptLink()}
                  >
                    {receiptCopied ? 'Copied' : 'Copy Link'}
                  </Button>
                ) : null}
                {waiverDocumentUrl ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    leftIcon={<FileText className="h-4 w-4" />}
                    onClick={() => openDocument(waiverDocumentUrl)}
                  >
                    Waiver
                  </Button>
                ) : null}
              </div>
            ) : null}
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <SelectFilter
              label="Payment status"
              value={formState.paymentStatus}
              options={[
                { value: 'PENDING', label: 'Pending' },
                { value: 'PAID', label: 'Paid' },
                { value: 'PARTIAL', label: 'Partial' },
                { value: 'WAIVED', label: 'Waived' },
                { value: 'REFUNDED', label: 'Refunded' },
                { value: 'FAILED', label: 'Failed' },
              ]}
              onChange={(value) =>
                update({
                  paymentStatus: value,
                  paidAt: value === 'PAID' && !formState.paidAt ? toDatetimeLocal(new Date().toISOString()) : formState.paidAt,
                  refundedAt: value === 'REFUNDED' && !formState.refundedAt ? toDatetimeLocal(new Date().toISOString()) : formState.refundedAt,
                  refundedAmount: value === 'REFUNDED' && !formState.refundedAmount ? formState.paidAmount : formState.refundedAmount,
                })
              }
            />
            <Input
              label="Method"
              placeholder="Zelle, check, cash, external link"
              value={formState.paymentMethodLabel}
              onChange={(event) => update({ paymentMethodLabel: event.target.value })}
            />
            <Input
              label="Amount paid"
              type="number"
              min="0"
              step="0.01"
              placeholder={registration.priceAmount ? String(registration.priceAmount) : '0.00'}
              value={formState.paidAmount}
              onChange={(event) => update({ paidAmount: event.target.value })}
            />
            <Input
              label="Paid date"
              type="datetime-local"
              value={formState.paidAt}
              onChange={(event) => update({ paidAt: event.target.value })}
            />
          </div>
          <details className="tk-disclosure mt-4" open={formState.paymentStatus === 'REFUNDED' || undefined}>
            <summary>Refunds, references & notes</summary>
            <div className="grid gap-4 md:grid-cols-2">
            <Input
              label="Refunded amount"
              type="number"
              min="0"
              step="0.01"
              placeholder="0.00"
              value={formState.refundedAmount}
              onChange={(event) => update({ refundedAmount: event.target.value })}
            />
            <Input
              label="Refund date"
              type="datetime-local"
              value={formState.refundedAt}
              onChange={(event) => update({ refundedAt: event.target.value })}
            />
            <Input
              label="Refund reason"
              placeholder="Reason for the refund"
              value={formState.refundReason}
              onChange={(event) => update({ refundReason: event.target.value })}
            />
            <Input
              label="Reference"
              placeholder="Check #, Zelle ID, invoice #"
              value={formState.externalReference}
              onChange={(event) => update({ externalReference: event.target.value })}
            />
            <Input
              label="Payment link"
              placeholder="https://..."
              value={formState.externalPaymentUrl}
              onChange={(event) => update({ externalPaymentUrl: event.target.value })}
            />
            <Input
              label="Receipt URL"
              placeholder="https://..."
              value={formState.receiptUrl}
              onChange={(event) => update({ receiptUrl: event.target.value })}
            />
            <Input
              label="Coach note"
              placeholder="Internal registration note"
              value={formState.note}
              onChange={(event) => update({ note: event.target.value })}
            />
          </div>

          <div className="mt-4">
            <Textarea
              label="Payment note"
              placeholder="Short payment context visible to staff"
              value={formState.paymentNote}
              onChange={(event) => update({ paymentNote: event.target.value })}
            />
          </div>

          </details>

          {error ? (
            <div className="mt-4 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm font-medium text-rose-700">
              {error}
            </div>
          ) : null}
        </div>

        <div className="flex flex-col-reverse gap-2 border-t border-border px-4 py-4 md:flex-row md:justify-end md:px-5">
          <Button type="button" variant="outline" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button type="button" leftIcon={<CreditCard className="h-4 w-4" />} onClick={() => void onSubmit()} isLoading={isSaving}>
            Save Payment
          </Button>
        </div>
      </div>
    </div>
  );
}

function MiniMoney({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0 rounded-lg border border-border bg-surface px-3 py-2">
      <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-text-subtle">{label}</p>
      <p className="mt-1 truncate text-sm font-semibold text-text">{value}</p>
    </div>
  );
}

function ProgramCatalogTable({
  programs,
  canManageSchedule,
  onOpenProgram,
}: {
  programs: TenantProgram[];
  canManageSchedule: boolean;
  onOpenProgram: (program: TenantProgram) => void;
}) {
  return <div className="grid gap-4 md:grid-cols-2">
    {programs.map(program=><ProgramCatalogMobileRow key={program.programId} program={program} canManageSchedule={canManageSchedule} onOpenProgram={onOpenProgram} />)}
  </div>;
}

function ProgramCatalogMobileRow({
  program,
  canManageSchedule,
  onOpenProgram,
}: {
  program: TenantProgram;
  canManageSchedule: boolean;
  onOpenProgram: (program: TenantProgram) => void;
}) {
  const summary = summarizeProgramOptions(program);
  const policy = program.registrationPolicy;
  const status = getCatalogStatus(program);

  return (
    <button
      type="button"
      disabled={!canManageSchedule}
      onClick={() => onOpenProgram(program)}
      className="w-full rounded-xl border border-border bg-surface p-5 text-left transition-colors hover:bg-surface-elevated/40 disabled:cursor-default"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-semibold text-text">{policy?.publicTitle || program.name}</p>
          <p className="mt-1 text-sm text-text-muted">{formatCatalogDateRange(program)}</p>
        </div>
        <StatusPill status={status} />
      </div>
      <div className="mt-3 grid gap-2 text-sm text-text-muted">
        <IconLine icon={<BookOpenCheck className="h-4 w-4" />} value={`${summary.sessionCount} weeks / ${summary.categoryCount} classes`} />
        {summary.locationLabel ? <IconLine icon={<MapPin className="h-4 w-4" />} value={summary.locationLabel} /> : null}
        <IconLine icon={<Users className="h-4 w-4" />} value={program.defaultCapacity ? `${program.defaultCapacity} slots` : 'Slots not set'} />
      </div>
    </button>
  );
}

function CatalogModal({
  open,
  title,
  subtitle,
  status,
  actionSlot,
  onClose,
  children,
}: {
  open: boolean;
  title: string;
  subtitle: string;
  status: CatalogStatusFilter;
  actionSlot?: ReactNode;
  onClose: () => void;
  children: ReactNode;
}) {
  useEffect(() => {
    if (!open) {
      return;
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [onClose, open]);

  if (!open) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/45 px-3 py-6 backdrop-blur-sm">
      <div className="flex max-h-[92vh] w-full max-w-5xl flex-col overflow-hidden rounded-xl border border-border bg-surface shadow-2xl">
        <div className="flex flex-col gap-3 border-b border-border px-4 py-4 md:flex-row md:items-center md:justify-between md:px-5">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <p className="text-lg font-semibold text-text">{title}</p>
              <StatusPill status={status} />
            </div>
            <p className="mt-1 truncate text-sm font-medium text-text-muted">{subtitle}</p>
          </div>
          <div className="flex items-center gap-2">
            {actionSlot}
            <button
              type="button"
              onClick={onClose}
              className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-border text-text-muted transition-colors hover:bg-surface-elevated hover:text-text"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
        <div className="overflow-y-auto p-4 md:p-5">{children}</div>
      </div>
    </div>
  );
}

function CatalogStat({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-lg border border-border bg-surface px-3 py-2">
      <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-text-subtle">{label}</p>
      <p className="text-lg font-semibold leading-none text-text">{value}</p>
    </div>
  );
}

function SelectFilter({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: Array<{ value: string; label: string }>;
  onChange: (value: string) => void;
}) {
  return (
    <label className="space-y-1.5">
      <span className="block text-sm font-medium text-text">{label}</span>
      <BorderlessSelect
        className="tk-filter w-full"
        value={value}
        onChange={(event) => onChange(event.target.value)}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </BorderlessSelect>
    </label>
  );
}

function EmptyCatalogState({
  title,
  description,
  actionLabel,
  disabled,
  onAction,
}: {
  title: string;
  description: string;
  actionLabel?: string;
  disabled?: boolean;
  onAction?: () => void;
}) {
  return (
    <div className="rounded-lg border border-dashed border-border bg-surface-elevated/30 p-6">
      <p className="font-semibold text-text">{title}</p>
      <p className="mt-1 max-w-xl text-sm text-text-muted">{description}</p>
      {actionLabel && onAction ? (
        <Button className="mt-4" size="sm" onClick={onAction} disabled={disabled}>
          {actionLabel}
        </Button>
      ) : null}
    </div>
  );
}

function IconLine({ icon, value, className }: { icon: ReactNode; value: string; className?: string }) {
  return (
    <div className={cn('flex min-w-0 items-center gap-2 text-sm text-text-muted', className)}>
      <span className="shrink-0 text-primary">{icon}</span>
      <span className="truncate font-medium text-text">{value}</span>
    </div>
  );
}

function StatusPill({ status }: { status: CatalogStatusFilter }) {
  const styles =
    status === 'published'
      ? 'border-primary/20 bg-primary/10 text-primary'
      : status === 'inactive'
        ? 'border-slate-200 bg-slate-100 text-slate-600'
        : 'border-border bg-surface-elevated/50 text-text-subtle';

  return (
    <span className={cn('tk-catalog-status rounded-full border px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.14em]', styles)}>
      {status === 'published' ? 'Published' : status === 'inactive' ? 'Inactive' : 'Draft'}
    </span>
  );
}

function RegistrationPill({ value, tone }: { value?: string | null; tone?: 'paid' | 'pending' }) {
  const normalized = (value || 'PENDING').toUpperCase();
  const styles =
    tone === 'paid' || normalized === 'REGISTERED' || normalized === 'PAID'
      ? 'border-primary/20 bg-primary/10 text-primary'
      : normalized === 'CANCELLED' || normalized === 'FAILED'
        ? 'border-rose-200 bg-rose-50 text-rose-700'
        : 'border-amber-200 bg-amber-50 text-amber-700';

  return (
    <span className={cn('tk-catalog-status rounded-full border px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.14em]', styles)}>
      {formatStatusLabel(normalized)}
    </span>
  );
}

function formatStatusLabel(value: string): string {
  return value
    .toLowerCase()
    .split('_')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function registrationNeedsAction(registration: TenantProgramRegistration): boolean {
  const registrationStatus = (registration.registrationStatus || '').toUpperCase();
  const paymentStatus = (registration.paymentStatus || '').toUpperCase();
  return ['REQUESTED', 'RECEIVED', 'WAITLISTED'].includes(registrationStatus) || ['PENDING', 'PARTIAL', 'FAILED'].includes(paymentStatus);
}

function matchesRegistrationStatus(registration: TenantProgramRegistration, filter: RegistrationStatusFilter): boolean {
  const status = (registration.registrationStatus || '').toUpperCase();
  if (filter === 'needs-action') {
    return registrationNeedsAction(registration);
  }
  if (filter === 'registered') {
    return status === 'REGISTERED';
  }
  if (filter === 'waitlisted') {
    return status === 'WAITLISTED';
  }
  if (filter === 'cancelled') {
    return status === 'CANCELLED';
  }
  return true;
}

function matchesPaymentStatus(registration: TenantProgramRegistration, filter: RegistrationPaymentFilter): boolean {
  const status = (registration.paymentStatus || '').toUpperCase();
  if (filter === 'due') {
    return moneyValue(registration.balanceAmount) > 0 || ['PENDING', 'PARTIAL', 'FAILED'].includes(status);
  }
  if (filter === 'paid') {
    return status === 'PAID' || status === 'WAIVED';
  }
  if (filter === 'partial') {
    return status === 'PARTIAL';
  }
  if (filter === 'refunded') {
    return status === 'REFUNDED';
  }
  return true;
}

function defaultPaymentForm(registration?: TenantProgramRegistration | null): PaymentFormState {
  const paymentStatus = (registration?.paymentStatus || 'PAID').toUpperCase();
  const fallbackAmount = registration?.amountDue ?? registration?.priceAmount;
  return {
    paymentStatus,
    paymentMethodLabel: registration?.paymentMethodLabel || '',
    paidAmount: registration?.paidAmount != null ? String(registration.paidAmount) : fallbackAmount != null ? String(fallbackAmount) : '',
    paidAt: registration?.paidAt ? toDatetimeLocal(registration.paidAt) : paymentStatus === 'PAID' ? toDatetimeLocal(new Date().toISOString()) : '',
    refundedAmount: registration?.refundedAmount != null ? String(registration.refundedAmount) : '',
    refundedAt: registration?.refundedAt ? toDatetimeLocal(registration.refundedAt) : '',
    externalReference: registration?.externalReference || '',
    externalPaymentUrl: registration?.externalPaymentUrl || '',
    receiptUrl: registration?.receiptUrl || '',
    refundReason: registration?.refundReason || '',
    paymentNote: registration?.paymentNote || '',
    note: registration?.note || '',
  };
}

function toDatetimeLocal(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  const offsetMs = date.getTimezoneOffset() * 60 * 1000;
  return new Date(date.getTime() - offsetMs).toISOString().slice(0, 16);
}

function formatRegistrationAmount(registration: TenantProgramRegistration): string {
  const status = (registration.paymentStatus || '').toUpperCase();
  const amountDue = moneyValue(registration.amountDue ?? registration.priceAmount);
  const balanceAmount = moneyValue(registration.balanceAmount);
  const refundedAmount = moneyValue(registration.refundedAmount);
  const paidAmount = moneyValue(registration.paidAmount ?? (status === 'PAID' ? amountDue : 0));

  if (status === 'REFUNDED' || refundedAmount > 0) {
    return `${formatMoney(refundedAmount || paidAmount, registration.currencyCode)} refunded`;
  }
  if (balanceAmount > 0) {
    return `${formatMoney(balanceAmount, registration.currencyCode)} due`;
  }
  if (paidAmount > 0 || status === 'PAID' || status === 'WAIVED') {
    return status === 'WAIVED' ? 'Waived' : `${formatMoney(paidAmount, registration.currencyCode)} paid`;
  }
  if (amountDue > 0) {
    return `${formatMoney(amountDue, registration.currencyCode)} due`;
  }
  return registration.priceLabel || 'No fee';
}

function buildReceiptDocumentUrl(registration: TenantProgramRegistration): string | null {
  if (!registration.receiptNumber) {
    return null;
  }
  return (
    buildPublicReceiptDocumentUrl(registration) ||
    buildApiDocumentUrl(`/api/v1/web/schedule/program-registrations/${registration.registrationId}/receipt`)
  );
}

function buildPublicReceiptDocumentUrl(registration: TenantProgramRegistration): string | null {
  if (!registration.receiptShareEnabled || !registration.receiptShareToken) {
    return null;
  }
  return buildApiDocumentUrl(
    `/api/v1/public/program-registration-documents/receipts/${encodeURIComponent(registration.receiptShareToken)}`
  );
}

function buildWaiverDocumentUrl(registration: TenantProgramRegistration): string | null {
  if (registration.waiverRecordId == null) {
    return null;
  }
  return buildApiDocumentUrl(`/api/v1/web/schedule/program-registrations/${registration.registrationId}/waiver`);
}

function buildApiDocumentUrl(path: string): string {
  const baseUrl = typeof apiClient.defaults.baseURL === 'string' && apiClient.defaults.baseURL
    ? apiClient.defaults.baseURL
    : typeof window !== 'undefined'
      ? window.location.origin
      : 'http://localhost:8080';
  return new URL(path, baseUrl).toString();
}

function formatPaymentMeta(registration: TenantProgramRegistration): string {
  const documents = [registration.invoiceNumber, registration.receiptNumber].filter(Boolean);
  if (documents.length) {
    return documents.join(' / ');
  }
  return [registration.paymentMethodLabel, registration.externalReference].filter(Boolean).join(' / ') || 'No payment details';
}

function summarizeRegistrationMoney(registrations: TenantProgramRegistration[]): RegistrationMoneySummary {
  return registrations.reduce<RegistrationMoneySummary>(
    (summary, registration) => {
      const registrationStatus = (registration.registrationStatus || '').toUpperCase();
      if (registrationStatus === 'CANCELLED') {
        return summary;
      }

      const paymentStatus = (registration.paymentStatus || '').toUpperCase();
      const amountDue = moneyValue(registration.amountDue ?? registration.priceAmount);
      const paidAmount = moneyValue(registration.paidAmount ?? (paymentStatus === 'PAID' ? amountDue : 0));
      const balanceAmount = moneyValue(registration.balanceAmount);
      const refundedAmount = moneyValue(registration.refundedAmount);
      const currencyCode = registration.currencyCode || summary.currencyCode || 'USD';

      summary.amountDue += amountDue;
      summary.paidAmount += paidAmount;
      summary.balanceAmount += balanceAmount;
      summary.refundedAmount += refundedAmount;
      summary.currencyCode = currencyCode;
      if (balanceAmount > 0 || ['PENDING', 'FAILED'].includes(paymentStatus)) {
        summary.dueCount += 1;
      }
      if (paymentStatus === 'PAID' || paymentStatus === 'WAIVED') {
        summary.paidCount += 1;
      }
      if (paymentStatus === 'PARTIAL') {
        summary.partialCount += 1;
      }
      if (paymentStatus === 'REFUNDED' || refundedAmount > 0) {
        summary.refundedCount += 1;
      }
      return summary;
    },
    {
      amountDue: 0,
      paidAmount: 0,
      balanceAmount: 0,
      refundedAmount: 0,
      dueCount: 0,
      paidCount: 0,
      partialCount: 0,
      refundedCount: 0,
      currencyCode: 'USD',
    }
  );
}

function moneyValue(value?: number | string | null): number {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : 0;
  }
  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
}

function formatMoney(value?: number | string | null, currencyCode?: string | null): string {
  return `${currencyCode || 'USD'} ${moneyValue(value).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function formatDateTime(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
}

function exportRegistrationsCsv(registrations: TenantProgramRegistration[]) {
  const headers = [
    'Student',
    'Parent',
    'Parent Email',
    'Parent Phone',
    'Class',
    'Session',
    'Category',
    'Days',
    'Registration Status',
    'Payment Status',
    'Price',
    'Invoice Number',
    'Receipt Number',
    'Receipt Generated At',
    'Receipt Share Enabled',
    'Receipt Share Token',
    'Amount Due',
    'Paid Amount',
    'Paid At',
    'Balance Amount',
    'Refunded Amount',
    'Refunded At',
    'Refund Reason',
    'Method',
    'External Reference',
    'Payment Link',
    'Receipt URL',
    'Waiver Accepted',
    'Waiver Record ID',
    'Waiver Version',
    'Waiver Title',
    'Waiver Hash',
    'Waiver Signed At',
    'Waiver Signer',
    'Waiver Relationship',
    'Roster Bookings',
    'Waitlist Bookings',
    'Created At',
    'Note',
  ];
  const rows = registrations.map((registration) => [
    registration.athleteDisplayName,
    registration.guardianDisplayName || '',
    registration.guardianEmail || '',
    registration.guardianPhone || '',
    registration.publicTitle || registration.programName || '',
    registration.selectedSessionLabel || '',
    registration.selectedCategoryLabel || '',
    registration.daysPreference || '',
    registration.registrationStatus || '',
    registration.paymentStatus || '',
    registration.priceLabel || '',
    registration.invoiceNumber || '',
    registration.receiptNumber || '',
    registration.receiptGeneratedAt || '',
    registration.receiptShareEnabled ? 'Yes' : 'No',
    registration.receiptShareToken || '',
    registration.amountDue != null ? String(registration.amountDue) : '',
    registration.paidAmount != null ? String(registration.paidAmount) : '',
    registration.paidAt || '',
    registration.balanceAmount != null ? String(registration.balanceAmount) : '',
    registration.refundedAmount != null ? String(registration.refundedAmount) : '',
    registration.refundedAt || '',
    registration.refundReason || '',
    registration.paymentMethodLabel || '',
    registration.externalReference || '',
    registration.externalPaymentUrl || '',
    registration.receiptUrl || '',
    registration.waiverAccepted ? 'Yes' : 'No',
    registration.waiverRecordId != null ? String(registration.waiverRecordId) : '',
    registration.waiverVersion || '',
    registration.waiverTitle || '',
    registration.waiverContentHash || '',
    registration.waiverSignedAt || '',
    registration.waiverSignerDisplayName || '',
    registration.waiverSignerRelationshipCode || '',
    registration.rosterBookingCount != null ? String(registration.rosterBookingCount) : '',
    registration.waitlistBookingCount != null ? String(registration.waitlistBookingCount) : '',
    registration.createdAt || '',
    registration.note || '',
  ]);
  const csv = [headers, ...rows]
    .map((row) => row.map((value) => `"${String(value).replace(/"/g, '""')}"`).join(','))
    .join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `tark-registrations-${new Date().toISOString().slice(0, 10)}.csv`;
  link.click();
  URL.revokeObjectURL(url);
}

function getCatalogStatus(program: TenantProgram): CatalogStatusFilter {
  if (!program.active) {
    return 'inactive';
  }
  return programIsPublished(program) ? 'published' : 'draft';
}

function buildCoachOptions(members: TenantMemberSummary[]): ProgramCatalogCoachOption[] {
  const coachRoles = new Set(['OWNER', 'ADMIN', 'COACH', 'STAFF', 'MANAGER']);
  const seen = new Set<number>();

  return members
    .filter((member) => member.userId && member.membershipStatus?.toUpperCase() === 'ACTIVE')
    .filter((member) => coachRoles.has((member.roleCode || '').toUpperCase()))
    .filter((member) => {
      if (seen.has(member.userId)) {
        return false;
      }
      seen.add(member.userId);
      return true;
    })
    .map((member) => ({
      userId: member.userId,
      label: member.displayName || member.profileName || member.email || `Coach ${member.userId}`,
      roleLabel: formatStatusLabel(member.roleCode || 'Coach'),
    }));
}
